import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useFormContext } from 'react-hook-form';
import { UploadIllustration } from 'src/assets/illustrations';
// import Iconify from '@components/iconify';

import Iconify from 'src/minimal-components/iconify';
import {
  CustomFile,
  RejectionFiles,
  UploadPropslms
} from 'src/minimal-components/upload';
import { AnimatePresence, m } from 'framer-motion';
import {
  fileDatas,
  fileFormat,
  fileThumb,
  fileTypeByUrl
} from 'src/minimal-components/file-thumbnail';
import { varFade } from 'src/minimal-components/animate';
import { Grid, LinearProgress, ListItemText, Tooltip } from '@mui/material';
import EncryptUtils from 'src/utils/EncryptUtils';
import * as APIService from '@shared/services/service-handlers/PrivateService';
import { useBoolean } from 'src/hooks/use-boolean';
import { ConfirmDialog } from '@components/custom-dialog';
// ----------------------------------------------------------------------
interface progressBarData {
  id?: string;
  progress: number;
  display: boolean;
}

export default function UploadFiles({
  disabled,
  multiple = false,
  name,
  error,
  helperText,
  //
  file,
  onDelete,
  //
  files,
  thumbnail,
  onUpload,
  onRemove,
  onRemoveAll,
  sx,
  ...other
}: UploadPropslms) {
  const { control, setError, setValue, watch } = useFormContext();
  const valueeee = watch();
  const presentfiles = watch('uploadResourcesfiles') || [];
  const confirm = useBoolean();
  const [uploadedFile, setUploadedFile] = useState<(string | CustomFile)[]>(
    files || []
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    fileRejections,
    acceptedFiles: rawAcceptedFiles
  } = useDropzone({
    multiple,
    disabled,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png'],
      'application/pdf': ['.pdf']
    },
    onDrop: files => {
      // Deduplicate files or folders
      if (multiple) {
        const uniqueFiles = files.reduce<File[]>((acc, file) => {
          if (
            !acc.find(
              f =>
                f.name === file.name &&
                f.size === file.size &&
                f.type === file.type
            )
          ) {
            acc.push(file);
          }
          return acc;
        }, []);
        return uniqueFiles; // Return deduplicated files
      } else {
        return files; // Return all files if not multiple
      }
    },
    ...other
  });
  const acceptedFiles = [
    ...new Map(rawAcceptedFiles.map(file => [file.name, file])).values()
  ];
  const [uploadProgress, setUploadProgress] = useState<progressBarData[]>([]);
  const [deletefile, setDeletedFile] = useState<string | CustomFile | null>(
    null
  );
  useEffect(() => {
    setUploadedFile(files || []);
  }, [presentfiles]);
  const uploadFileToServer = async (file: string | CustomFile) => {
    try {
      const { id, fileup, type, endName } = fileDatas(file) || {
        id: '',
        fileup: '',
        type: 0,
        endName: ''
      };

      const formData = new FormData();
      if (fileup && type && endName) {
        const reqPayload = EncryptUtils.encryptedData({
          userId: `${id}.${endName}`
        });
        formData.append('file', fileup);
        formData.append('tk', reqPayload);
      }
      const response = await APIService.postUploadData(
        'LMS',
        'module',
        formData,
        'uploadFile',
        progressEvent => {
          if (progressEvent.total) {
            const total = progressEvent.total ?? 0;
            const progress =
              total > 0 ? Math.round((progressEvent.loaded / total) * 100) : 0;

            setUploadProgress((prev: progressBarData[]) => {
              const itemExists = prev.some(item => item.id === id);

              if (itemExists) {
                return prev.map(item =>
                  item.id === id
                    ? { ...item, progress: progress, display: true }
                    : item
                );
              } else {
                return [...prev, { id, progress: progress, display: true }];
              }
            });
          }
        }
      );
      setUploadProgress(prev =>
        prev.map(item => (item.id === id ? { ...item, display: false } : item))
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };
  const handleUploadAll = async (
    files: (string | CustomFile)[] | undefined
  ) => {
    if (files) {
      const updatedFiles = valueeee?.bloBurls || [];

      for (const file of files) {
        const {
          id,
          name = '',
          size = 0,
          type = ''
        } = fileDatas(file) || {id: '', name: '', size: 0, type: ''};

        const isFileAlreadyUploaded = uploadProgress.some(
          progress => progress.id === id
        );

        if (!isFileAlreadyUploaded) {
          const blobUrl = await uploadFileToServer(file); // Wait for each file to finish before starting the next

          // Update the file object with the blob URL
          const existingFileIndex = updatedFiles.findIndex(
            (item: { id: any }) => item.id === id
          );

          if (existingFileIndex !== -1) {
            // Update the existing file's blob URL
            updatedFiles[existingFileIndex].blobUrl = blobUrl;
          } else {
            // Add the new file with its blob URL
            updatedFiles.push({
              id: id,
              name: name,
              type: type,
              size: size * 1024 * 1024,
              blobUrl: blobUrl,
              fileExtension: fileTypeByUrl(name)
            });
          }

          await setValue('bloBurls', updatedFiles, {
            shouldValidate: true
          });
          // Update the form value with the updated files array
        } else {
          console.log(
            `File with ID: ${id} is already in progress or uploaded.`
          );
        }
        await setValue('displayprogress', uploadProgress);
      }
    }
  };
  const init = (files: CustomFile[]) => {
    if (files) {
      setUploadedFile(prev => {
        const newFiles = files.filter(
          newFile =>
            !prev.some(filesadd => {
              const fileData = fileDatas(filesadd) || { id: '' }; // Ensure fileData exists
              return fileData.id === newFile?.id; // Proper comparison
            })
        );

        return [...prev, ...newFiles]; // Append only unique files
      });

      const newFiles = files.filter(file => {
        const { id } = fileDatas(file) || { id: '' }; // Use `fileDatas` to extract `id`

        return !uploadProgress.some(progress => progress.id === id);
      });
      const initialProgress = newFiles.map(file => {
        const { id } = fileDatas(file) || { id: '' }; // Use `fileDatas` to extract `id`
        return {
          id: id,
          progress: 0,
          display: true
        };
      });
      setUploadProgress(prev => [...prev, ...initialProgress]);
    }
    handleUploadAll(files);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!multiple) {
        try {
          const uploadedFiles = watch('bloBurls') || [];
          const validfiles = acceptedFiles;
          const filesInAccepted: CustomFile[] = files?.filter(file => {
            const { name = '' } = fileDatas(file) || { id: '', name: '' };
            return validfiles.some(acceptedFile => acceptedFile.name === name);
          }) as CustomFile[];
          const filesToProcess = filesInAccepted?.filter(file => {
            const { id } = fileDatas(file) || { id: '' }; // Default `id` to an empty string
            return !uploadedFiles.some(
              (uploadedFile: { id?: string }) => uploadedFile.id === id
            );
          });
          if (filesToProcess && filesToProcess?.length > 0) {
            // Pass all filtered files to init at once
            await init(filesToProcess);
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      }
    };
    fetchData();
  }, [rawAcceptedFiles, files, acceptedFiles, uploadedFile]);
  const renderPlaceholder = (
    <Stack
      spacing={3}
      alignItems="center"
      justifyContent="center"
      flexWrap="wrap"
      sx={{ cursor: 'pointer' }}
      onClick={event => {
        if (uploadedFile && uploadedFile.length > 0) {
          if (name && multiple && uploadedFile.length >= 3) {
            setError(name, {
              message: 'Maximum file limit has been reached'
            });
          } else {
            if (name && !multiple && uploadedFile.length >= 1) {
              setError(name, {
                message:
                  'A file is already uploaded. Remove it to upload a new file.'
              });
            }
          }
        }
      }}>
      <UploadIllustration sx={{ width: 1, maxWidth: 200 }} />
      <Stack spacing={1} sx={{ textAlign: 'center' }}>
        <Typography variant="h6">Drop or Select file</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Drop files here or click
          <Box
            component="span"
            sx={{
              mx: 0.5,
              color: 'primary.main',
              textDecoration: 'underline'
            }}>
            browse
          </Box>
          through your machine
        </Typography>
      </Stack>
    </Stack>
  );

  const renderSinglePreviewThumbnail = (
    <Grid container spacing={2} sx={{ mt: 2, px: 2 }}>
      <AnimatePresence initial={false}>
        {files?.map(file => {
          const {
            id = '',
            name = '',
            size = 0,
            path = '',
            preview = ''
          } = fileDatas(file) || {
            id: '',
            name: '',
            size: 0,
            path: '',
            preview: ''
          };

          const isNotFormatFile = typeof file === 'string';
          const format = fileFormat(name);
          if (thumbnail) {
            return (
              <Grid
                key={id}
                component={m.div}
                item
                xs={12}
                md={10}
                {...varFade().inUp}
                sx={{
                  m: 0.5,
                  padding: 0.5,
                  borderRadius: 1.25,
                  overflow: 'hidden',
                  position: 'relative',
                  display: '-webkit-box',
                  paddingTop: '8px !important',
                  border: theme =>
                    `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                  ...sx
                }}>
                <Grid
                  alignItems="center"
                  justifyContent="center"
                  display="flex"
                  item
                  xs={12}
                  md={2}>
                  <Tooltip title={name}>
                    <Stack
                      flexShrink={0}
                      component="span"
                      alignItems="center"
                      justifyContent="center"
                      display="flex"
                      sx={{
                        width: 'fit-content',
                        height: 'inherit'
                      }}>
                      <Box
                        component="img"
                        src={
                          format === 'dcm'
                            ? '/assets/icons/files/dcm.png'
                            : fileThumb(format)
                        }
                        sx={{
                          width: 32,
                          height: 32,
                          flexShrink: 0,
                          ...sx
                        }}
                      />
                    </Stack>
                  </Tooltip>
                </Grid>
                <Grid item xs={12} md={10}>
                  <ListItemText
                    sx={{ padding: '1vh' }}
                    primary={
                      <Typography
                        variant="h4"
                        noWrap
                        sx={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap'
                        }}>
                        {isNotFormatFile ? file : name}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        {uploadProgress &&
                          uploadProgress.find(
                            item => item.id === id && item.display
                          ) && (
                            <LinearProgress
                              variant="determinate"
                              value={
                                uploadProgress.find(
                                  progress => progress.id === id
                                )?.progress || 0
                              }
                              sx={{ margin: '8px 0', borderRadius: '4px' }}
                            />
                          )}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ display: 'block', textAlign: 'right' }}>
                          {isNotFormatFile ? '' : `${size.toFixed(2)} MB`}
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
                {onRemove && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (id) {
                        setDeletedFile(file);
                        confirm.onTrue();
                      }
                    }}
                    sx={{
                      p: 0.5,
                      top: 4,
                      right: 4,
                      position: 'absolute',
                      color: 'common.white',
                      bgcolor: theme => alpha(theme.palette.grey[900], 0),
                      '&:hover': {
                        bgcolor: theme => alpha(theme.palette.grey[900], 0.72)
                      }
                    }}>
                    <Iconify icon="mingcute:close-line" width={14} />
                  </IconButton>
                )}
              </Grid>
            );
          }
        })}
      </AnimatePresence>
    </Grid>
  );

  return (
    <Grid sx={{ width: 1, position: 'relative', ...sx }}>
      <Grid
        {...(multiple && uploadedFile?.length
          ? uploadedFile?.length < 3 && getRootProps()
          : uploadedFile?.length === 0 && getRootProps())}
        sx={{
          p: 2,
          outline: 'none',
          borderRadius: 1,
          overflow: 'hidden',
          position: 'relative',
          bgcolor: theme => alpha(theme.palette.grey[500], 0.08),
          border: theme => `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
          transition: theme => theme.transitions.create(['opacity', 'padding']),
          '&:hover': {
            opacity: 0.72
          },
          ...(isDragActive && {
            opacity: 0.72
          }),
          ...(disabled && {
            opacity: 0.48,
            pointerEvents: 'none'
          })
        }}>
        {
          <input
            {...(multiple && uploadedFile?.length
              ? uploadedFile?.length < 4 && getInputProps()
              : uploadedFile?.length === 0 && getInputProps())}
            style={{
              display: uploadedFile?.length
                ? multiple && uploadedFile?.length >= 3
                  ? 'none'
                  : uploadedFile?.length !== 0 && !multiple
                    ? 'none'
                    : 'none'
                : 'none'
            }}
          />
        }
        {renderPlaceholder}
      </Grid>

      {helperText && helperText}

      <RejectionFiles fileRejections={fileRejections} />
      {thumbnail && renderSinglePreviewThumbnail}
      {onRemove && (
        <ConfirmDialog
          open={confirm.value}
          onClose={confirm.onFalse}
          title="Delete"
          content="Are you sure want to delete?"
          action={
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (deletefile) {
                  const { id } = fileDatas(deletefile) || { id: '' };
                  if (id !== '') {
                    setUploadProgress((prev: progressBarData[]) =>
                      prev.filter(item => {
                        if (typeof item === 'object' && 'id' in item) {
                          return item.id !== id;
                        }
                        return true; // Keep the item if it's a string
                      })
                    );
                    setUploadedFile((prev: (string | CustomFile)[]) =>
                      prev.filter(item => {
                        if (typeof item === 'object' && 'id' in item) {
                          return item.id !== id;
                        }
                        return true; // Keep the item if it's a string
                      })
                    );
                  }
                  confirm.onFalse();
                  // Call onRemove with the id
                  onRemove(deletefile);
                  setDeletedFile(null);
                }
              }}>
              Delete
            </Button>
          }
        />
      )}
    </Grid>
  );
}
